/**
 * Client-side encryption utilities for sensitive data
 * Uses Web Crypto API for secure encryption
 */

// Generate a random encryption key
export const generateEncryptionKey = async (): Promise<CryptoKey> => {
  return await window.crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256
    },
    true,
    ['encrypt', 'decrypt']
  );
};

// Export key to string for storage
export const exportKey = async (key: CryptoKey): Promise<string> => {
  const exported = await window.crypto.subtle.exportKey('raw', key);
  return btoa(String.fromCharCode(...new Uint8Array(exported)));
};

// Import key from string
export const importKey = async (keyString: string): Promise<CryptoKey> => {
  const keyData = Uint8Array.from(atob(keyString), c => c.charCodeAt(0));
  return await window.crypto.subtle.importKey(
    'raw',
    keyData,
    'AES-GCM',
    true,
    ['encrypt', 'decrypt']
  );
};

// Encrypt data
export const encryptData = async (data: unknown, key: CryptoKey): Promise<{ encrypted: string; iv: string }> => {
  const encoder = new TextEncoder();
  const dataString = JSON.stringify(data);
  const encodedData = encoder.encode(dataString);
  
  // Generate random IV
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  
  // Encrypt
  const encryptedData = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv
    },
    key,
    encodedData
  );
  
  return {
    encrypted: btoa(String.fromCharCode(...new Uint8Array(encryptedData))),
    iv: btoa(String.fromCharCode(...iv))
  };
};

// Decrypt data
export const decryptData = async (encryptedString: string, ivString: string, key: CryptoKey): Promise<unknown> => {
  const encryptedData = Uint8Array.from(atob(encryptedString), c => c.charCodeAt(0));
  const iv = Uint8Array.from(atob(ivString), c => c.charCodeAt(0));
  
  const decryptedData = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: iv
    },
    key,
    encryptedData
  );
  
  const decoder = new TextDecoder();
  const decryptedString = decoder.decode(decryptedData);
  return JSON.parse(decryptedString);
};

// Session key management
let sessionKey: CryptoKey | null = null;

export const getSessionKey = async (): Promise<CryptoKey> => {
  if (!sessionKey) {
    // Try to get from sessionStorage
    const storedKey = sessionStorage.getItem('encryptionKey');
    if (storedKey) {
      sessionKey = await importKey(storedKey);
    } else {
      // Generate new key
      sessionKey = await generateEncryptionKey();
      const exportedKey = await exportKey(sessionKey);
      sessionStorage.setItem('encryptionKey', exportedKey);
    }
  }
  return sessionKey;
};

// Clear session key (on logout)
export const clearSessionKey = () => {
  sessionKey = null;
  sessionStorage.removeItem('encryptionKey');
};

// Encrypted cache implementation
export class EncryptedCache<T> {
  private cache = new Map<string, { encrypted: string; iv: string }>();
  
  async set(key: string, value: T): Promise<void> {
    const cryptoKey = await getSessionKey();
    const encrypted = await encryptData(value, cryptoKey);
    this.cache.set(key, encrypted);
  }
  
  async get(key: string): Promise<T | null> {
    const encrypted = this.cache.get(key);
    if (!encrypted) return null;
    
    try {
      const cryptoKey = await getSessionKey();
      return await decryptData(encrypted.encrypted, encrypted.iv, cryptoKey);
    } catch (error) {
      console.error('Error decrypting cache data:', error);
      // Remove corrupted entry
      this.cache.delete(key);
      return null;
    }
  }
  
  clear(): void {
    this.cache.clear();
  }
  
  get size(): number {
    return this.cache.size;
  }
  
  has(key: string): boolean {
    return this.cache.has(key);
  }
}